import { useState, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, Outlet, Link } from "react-router-dom";
import { ResizablePanelGroup, ResizablePanel, ResizableHandle } from "@/components/ui/resizable";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { UserDropdownMenu } from "@/components/UserDropdownMenu";
import { Sidebar } from "@/components/Sidebar";
import { ChatPanel } from "@/components/chat";
import { useQuery } from "@apollo/client";
import { GetCurrentUserDocument } from "@/graphql/getCurrentUser.api";
import { GetPortfoliosDocument } from "@/graphql/getPortfolios.api";
import { GetRiskCheckDocument } from "@/graphql/getRiskCheck.api";
import { cn } from "@/lib/utils";
import { BRAND_COLOR } from "@/lib/constants";
import { ChevronLeft, Zap } from "lucide-react";
import { DashboardChatProvider } from "@/contexts/DashboardChatContext";
import { DashboardHeaderActions } from "@/components/DashboardHeaderActions";
import type { ImperativePanelHandle } from "react-resizable-panels";

const SIDEBAR_MAX_SIZE = 25;
const SIDEBAR_DEFAULT_SIZE = 13;
const SIDEBAR_COLLAPSED_SIZE = 3;
const STORAGE_KEY = "sidebar-size";
const COLLAPSED_KEY = "sidebar-collapsed";

const CHAT_PANEL_KEY = "chat-panel-open";
const CHAT_SIZE_KEY = "chat-panel-size";
const CHAT_DEFAULT_SIZE = 25;
const CHAT_MIN_SIZE = 15;
const CHAT_MAX_SIZE = 50;

const HIDE_CHAT_PATHS = ["/dashboard", "/portfolio/new"];

const PAGE_KEYS: Record<string, string> = {
  dashboard: "dashboard:header.dashboard",
  analytics: "dashboard:header.analytics",
  risks: "dashboard:header.risks",
  transactions: "dashboard:header.transactions",
  settings: "dashboard:header.settings",
};

const SUB_PAGE_KEYS: Record<string, Record<string, string>> = {
  risks: {
    simulator: "dashboard:header.simulator",
  },
  analytics: {
    taxes: "dashboard:header.taxes",
  },
};

interface PageInfo {
  slug: string | null;
  pageName: string | null;
  pageHref: string | null;
  subPageName: string | null;
  riskCheckId: string | null;
}

function getPageInfo(pathname: string, t: (key: string) => string): PageInfo {
  const segments = pathname.split("/").filter(Boolean);

  if (segments[0] === "portfolio" && segments[1] === "new") {
    return { slug: null, pageName: t("dashboard:header.newPortfolio"), pageHref: null, subPageName: null, riskCheckId: null };
  }

  if (segments[0] === "portfolio" && segments[1]) {
    const slug = segments[1];
    const section = segments[2];
    const subSection = segments[3];
    const pageKey = section ? PAGE_KEYS[section] : "dashboard:header.holdingSummary";
    const subPageKey = section && subSection ? SUB_PAGE_KEYS[section]?.[subSection] : null;

    // For dynamic sub-pages (e.g. /risks/:scenarioName), use scenario title from i18n
    let subPageName: string | null = null;
    if (subPageKey) {
      subPageName = t(subPageKey);
    } else if (section === "risks" && subSection) {
      subPageName = t(`scenario:scenarios.${subSection}.title`);
    }

    const pageHref = subSection && section ? `/portfolio/${slug}/${section}` : null;

    return {
      slug,
      pageName: pageKey ? t(pageKey) : null,
      pageHref,
      subPageName,
      riskCheckId: null,
    };
  }

  if (segments[0] === "risk-checks") {
    if (segments[1] === "new") {
      return { slug: null, pageName: t("riskCheck:list.title"), pageHref: "/risk-checks", subPageName: t("riskCheck:list.createNew"), riskCheckId: null };
    }
    if (segments[1]) {
      return { slug: null, pageName: t("riskCheck:list.title"), pageHref: "/risk-checks", subPageName: null, riskCheckId: segments[1] };
    }
    return { slug: null, pageName: t("riskCheck:list.title"), pageHref: null, subPageName: null, riskCheckId: null };
  }

  if (segments[0] === "api-keys") {
    return {
      slug: null,
      pageName: t("apiKeys:page.title"),
      pageHref: null,
      subPageName: null,
      riskCheckId: null,
    };
  }

  if (segments[0] === "dashboard") {
    return { slug: null, pageName: t("dashboard:sidebar.dashboard"), pageHref: null, subPageName: null, riskCheckId: null };
  }

  return { slug: null, pageName: null, pageHref: null, subPageName: null, riskCheckId: null };
}

function getUserInitials(fullName: string | undefined | null): string {
  if (!fullName) return "U";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function MainLayout() {
  const { t } = useTranslation(["common", "dashboard", "scenario", "riskCheck", "apiKeys"]);

  const footer = (
    <footer className="border-t border-slate-100 py-4 text-center">
      <p className="text-xs text-neutral-400 whitespace-pre-line">
        {t("footer.disclaimer", { year: new Date().getFullYear() })}
      </p>
    </footer>
  );
  const location = useLocation();
  const sidebarRef = useRef<ImperativePanelHandle>(null);
  const chatPanelRef = useRef<ImperativePanelHandle>(null);

  const hideChatPanel = HIDE_CHAT_PATHS.includes(location.pathname);
  const isDashboard = location.pathname === "/dashboard";

  const { data: userData } = useQuery(GetCurrentUserDocument);
  const currentUser = userData?.currentUser;

  const { data: portfoliosData } = useQuery(GetPortfoliosDocument);
  const portfolios = useMemo(() => portfoliosData?.portfolios ?? [], [portfoliosData]);

  const { slug, pageName, pageHref, subPageName, riskCheckId } = getPageInfo(
    location.pathname, t
  );
  const currentPortfolio = useMemo(
    () => (slug ? portfolios.find((p) => p.slug === slug) : null),
    [slug, portfolios]
  );

  // Fetch risk check name for breadcrumb (uses Apollo cache if already loaded)
  const { data: riskCheckData } = useQuery(GetRiskCheckDocument, {
    variables: { id: riskCheckId! },
    skip: !riskCheckId,
  });
  const riskCheckName = riskCheckData?.riskCheck?.name ?? null;
  const effectiveSubPageName = riskCheckName || subPageName;

  const userInitials = getUserInitials(currentUser?.fullName);

  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(COLLAPSED_KEY);
    return stored === "true";
  });

  const [sidebarSize, setSidebarSize] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? parseFloat(stored) : SIDEBAR_DEFAULT_SIZE;
  });

  const [isChatOpen, setIsChatOpen] = useState(() => {
    const stored = localStorage.getItem(CHAT_PANEL_KEY);
    return stored === "true";
  });

  const [chatSize, setChatSize] = useState(() => {
    const stored = localStorage.getItem(CHAT_SIZE_KEY);
    return stored ? parseFloat(stored) : CHAT_DEFAULT_SIZE;
  });

  const handleToggleChat = () => {
    const panel = chatPanelRef.current;
    if (panel) {
      if (isChatOpen) {
        panel.collapse();
      } else {
        panel.expand();
      }
    }
  };

  const handleChatCollapse = () => {
    setIsChatOpen(false);
    localStorage.setItem(CHAT_PANEL_KEY, "false");
  };

  const handleChatExpand = () => {
    setIsChatOpen(true);
    localStorage.setItem(CHAT_PANEL_KEY, "true");
  };

  const handleChatResize = (size: number) => {
    if (size > 0) {
      setChatSize(size);
      localStorage.setItem(CHAT_SIZE_KEY, size.toString());
    }
  };

  const handleCollapse = () => {
    const panel = sidebarRef.current;
    if (panel) {
      if (isCollapsed) {
        const expandSize = sidebarSize > SIDEBAR_COLLAPSED_SIZE ? sidebarSize : SIDEBAR_DEFAULT_SIZE;
        panel.resize(expandSize);
        setIsCollapsed(false);
        localStorage.setItem(COLLAPSED_KEY, "false");
      } else {
        panel.resize(SIDEBAR_COLLAPSED_SIZE);
        setIsCollapsed(true);
        localStorage.setItem(COLLAPSED_KEY, "true");
      }
    }
  };

  const handleResize = (size: number) => {
    if (size <= SIDEBAR_COLLAPSED_SIZE) {
      setIsCollapsed(true);
      localStorage.setItem(COLLAPSED_KEY, "true");
    } else {
      setIsCollapsed(false);
      localStorage.setItem(COLLAPSED_KEY, "false");
      setSidebarSize(size);
      localStorage.setItem(STORAGE_KEY, size.toString());
    }
  };

  return (
    <DashboardChatProvider>
    <TooltipProvider delayDuration={0}>
      <div className="h-screen bg-background">
        <ResizablePanelGroup direction="horizontal" className="h-full">
          {/* Sidebar */}
          <ResizablePanel
            ref={sidebarRef}
            defaultSize={isCollapsed ? SIDEBAR_COLLAPSED_SIZE : sidebarSize}
            minSize={SIDEBAR_COLLAPSED_SIZE}
            maxSize={SIDEBAR_MAX_SIZE}
            onResize={handleResize}
            className={cn(
              "bg-card border-r border-border transition-all duration-200",
              isCollapsed && "min-w-[48px] max-w-[48px]"
            )}
          >
            <aside className="h-full flex flex-col">
              {/* Logo */}
              <div
                className={cn(
                  "flex items-center h-12 px-4 border-b border-border",
                  isCollapsed && "justify-center px-0"
                )}
              >
                <div className={cn("flex items-center gap-2", isCollapsed && "justify-center")}>
                  <MountainIcon className="h-5 w-5 text-neutral-800 shrink-0" />
                  {!isCollapsed && (
                    <span className="text-[15px] font-semibold text-neutral-900 whitespace-nowrap">
                      A<span style={{ color: BRAND_COLOR }}>x</span>entiq
                    </span>
                  )}
                </div>
              </div>

              {/* Navigation */}
              <div className="flex-1 overflow-y-auto p-3">
                <Sidebar isCollapsed={isCollapsed} portfolios={portfolios} />
              </div>

              {/* Collapse Toggle */}
              <div
                className={cn(
                  "p-3 border-t border-border",
                  isCollapsed && "flex justify-center"
                )}
              >
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleCollapse}
                      className={cn(
                        "w-full justify-start gap-2 text-muted-foreground",
                        isCollapsed && "w-8 h-8 p-0 justify-center"
                      )}
                    >
                      <ChevronLeft
                        size={16}
                        className={cn(
                          "transition-transform",
                          isCollapsed && "rotate-180"
                        )}
                      />
                      {!isCollapsed && (
                        <span className="text-sm">{t("sidebar.collapse")}</span>
                      )}
                    </Button>
                  </TooltipTrigger>
                  {isCollapsed && (
                    <TooltipContent side="right" className="font-medium">
                      {t("sidebar.expand")}
                    </TooltipContent>
                  )}
                </Tooltip>
              </div>
            </aside>
          </ResizablePanel>

          {/* Resize Handle */}
          <ResizableHandle className="w-px bg-transparent hover:bg-neutral-300 transition-colors" />

          {/* Main Content Area */}
          <ResizablePanel defaultSize={100 - (isCollapsed ? SIDEBAR_COLLAPSED_SIZE : sidebarSize)}>
            <div className="h-full flex flex-col">
              {/* Header */}
              <header className="h-12 bg-background/95 backdrop-blur border-b border-border flex items-center justify-between px-6 shrink-0 sticky top-0 z-10">
                {/* Left side - Breadcrumb */}
                <div className="flex items-center gap-3">
                  {currentPortfolio ? (
                    <>
                      <h1 className="text-lg font-bold text-neutral-900">{currentPortfolio.name}</h1>
                      {pageName && (
                        <>
                          <span className="text-neutral-300">/</span>
                          {pageHref ? (
                            <Link
                              to={pageHref}
                              className="text-neutral-500 font-medium hover:text-neutral-700 transition-colors"
                            >
                              {pageName}
                            </Link>
                          ) : (
                            <span className="text-neutral-500 font-medium">{pageName}</span>
                          )}
                        </>
                      )}
                      {effectiveSubPageName && (
                        <>
                          <span className="text-neutral-300">/</span>
                          <span className="text-neutral-500 font-medium">{effectiveSubPageName}</span>
                        </>
                      )}
                    </>
                  ) : pageName ? (
                    <>
                      {pageHref ? (
                        <Link to={pageHref} className="text-lg font-bold text-neutral-900 hover:text-neutral-700 transition-colors">
                          {pageName}
                        </Link>
                      ) : (
                        <h1 className="text-lg font-bold text-neutral-900">{pageName}</h1>
                      )}
                      {effectiveSubPageName && (
                        <>
                          <span className="text-neutral-300">/</span>
                          <span className="text-neutral-500 font-medium">{effectiveSubPageName}</span>
                        </>
                      )}
                    </>
                  ) : null}
                </div>

                {/* Right side */}
                <div className="flex items-center gap-3">
                  {isDashboard && <DashboardHeaderActions />}
                  {currentUser && (
                    <UserDropdownMenu
                      fullName={currentUser.fullName}
                      email={currentUser.email}
                      initials={userInitials}
                    />
                  )}
                  {!hideChatPanel && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleToggleChat}
                      className="gap-1.5 px-3 py-1.5 h-auto border-orange-200 bg-orange-50 hover:bg-orange-100 hover:border-orange-300 rounded-full"
                    >
                      <Zap className="h-4 w-4 text-orange-500 fill-orange-500" />
                      <span className="text-sm font-bold text-orange-600">
                        A<span style={{ color: BRAND_COLOR }}>X</span>
                      </span>
                    </Button>
                  )}
                </div>
              </header>

              {/* Main Content with Chat Panel */}
              {hideChatPanel ? (
                <main className="flex-1 overflow-auto flex flex-col">
                  <div className="flex-1">
                    <Outlet />
                  </div>
                  {footer}
                </main>
              ) : (
                <ResizablePanelGroup direction="horizontal" className="flex-1" autoSaveId="chat-panel-layout">
                  <ResizablePanel defaultSize={100 - (isChatOpen ? chatSize : 0)} minSize={30}>
                    <main className="h-full overflow-auto flex flex-col">
                      <div className="flex-1">
                        <Outlet />
                      </div>
                      {footer}
                    </main>
                  </ResizablePanel>
                  <ResizableHandle className="w-px bg-neutral-200 hover:bg-neutral-400 transition-colors data-[panel-group-direction=horizontal]:cursor-col-resize" />
                  <ResizablePanel
                    ref={chatPanelRef}
                    defaultSize={isChatOpen ? chatSize : 0}
                    minSize={CHAT_MIN_SIZE}
                    maxSize={CHAT_MAX_SIZE}
                    collapsible
                    collapsedSize={0}
                    onCollapse={handleChatCollapse}
                    onExpand={handleChatExpand}
                    onResize={handleChatResize}
                    className="transition-all duration-300 ease-in-out"
                  >
                    <ChatPanel onClose={handleToggleChat} isOpen={isChatOpen} />
                  </ResizablePanel>
                </ResizablePanelGroup>
              )}
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
      </div>
    </TooltipProvider>
    </DashboardChatProvider>
  );
}

function MountainIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 32 32"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 26L12 10L16 18L22 6L28 26H4Z" />
    </svg>
  );
}
